import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { getFollowingsSettingsLogs } from 'services/actions/followingsService';
import { ArrowUpDown, Info, Inbox, Loader2 } from 'lucide-react';
import Spinner from 'components/common/Spinner';
import { logger } from 'utils/logger';

interface FollowingsLogsProps {
  selectedUser: string;
  switchToSettings: () => void;
}

interface LogEntry {
  created_at?: string;
  rule_info?: { rule_name?: string };
  username?: string;
  action_description?: string;
  [key: string]: unknown;
}

interface LogsPagination {
  page: number;
  totalPages: number;
}

interface FollowerStats {
  calculated_percentage?: number;
  follower_ratio?: number;
}

interface FollowingsLogsResponse {
  data?: LogEntry[];
  follow_logs?: LogEntry[];
  pagination?: LogsPagination;
  follower_stats?: FollowerStats;
}

const PAGE_SIZE = 50;

const FollowingsLogs: React.FC<FollowingsLogsProps> = ({ selectedUser, switchToSettings }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const cellTextColor = isDark ? '#CAD5E2' : '#45556C';
  const linkColor = isDark ? '#51A2FF' : '#2B7FFF';
  const tableBorder = isDark ? '#1D293D' : '#F1F5F9';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const inputIcon = isDark ? '#62748E' : '#90A1B9';
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoBorder = isDark ? '#1E40AF' : '#93C5FD';
  const infoText = isDark ? '#93C5FD' : '#1E40AF';

  const isMountedRef = useRef(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false);
  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

  const fetchLogs = useCallback(async (pageNum: number, silent = false, append = false) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (!silent && !append) { setLoading(true); setError(null); }
      if (append) setLoadingMore(true);
      const dateParam = selectedDate !== 'all' ? selectedDate : undefined;
      const data = (await getFollowingsSettingsLogs(selectedUser, pageNum, PAGE_SIZE, dateParam)) as FollowingsLogsResponse | LogEntry[];
      if (isMountedRef.current) {
        const items: LogEntry[] = Array.isArray(data)
          ? data
          : (data.data ?? data.follow_logs ?? []);
        const pagination = Array.isArray(data) ? undefined : data.pagination;
        if (append) {
          setLogs(prev => [...prev, ...items]);
        } else {
          setLogs(Array.isArray(items) ? items : []);
        }
        const stats = Array.isArray(data) ? undefined : data.follower_stats;
        setLimitReached((stats?.calculated_percentage ?? 0) >= (stats?.follower_ratio ?? Infinity));
        if (pagination) {
          setHasMore(pagination.page < pagination.totalPages);
        } else {
          setHasMore(false);
        }
      }
    } catch (error: unknown) {
      if (isMountedRef.current && !silent) {
        logger.error('Error fetching following logs:', error);
        setError(t('followingsLogs.errors.loadFailed'));
      }
    } finally {
      isLoadingRef.current = false;
      if (isMountedRef.current) {
        if (!silent && !append) setLoading(false);
        if (append) setLoadingMore(false);
      }
    }
  }, [selectedUser, selectedDate, t]);

  useEffect(() => {
    if (selectedUser) {
      setPage(1);
      setHasMore(true);
      setLogs([]);
      fetchLogs(1);
    }
  }, [selectedUser, selectedDate, fetchLogs]);

  // Silent auto-refresh every 30 seconds
  useEffect(() => {
    if (!selectedUser) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !isLoadingRef.current) {
        const dateParam = selectedDate !== 'all' ? selectedDate : undefined;
        getFollowingsSettingsLogs(selectedUser, 1, page * PAGE_SIZE, dateParam)
          .then((data: FollowingsLogsResponse | LogEntry[]) => {
            if (isMountedRef.current) {
              const items: LogEntry[] = Array.isArray(data)
                ? data
                : (data.data ?? data.follow_logs ?? []);
              const pagination = Array.isArray(data) ? undefined : data.pagination;
              setLogs(items);
              const stats = Array.isArray(data) ? undefined : data.follower_stats;
              setLimitReached((stats?.calculated_percentage ?? 0) >= (stats?.follower_ratio ?? Infinity));
              if (pagination) setHasMore(pagination.page < pagination.totalPages);
            }
          })
          .catch(() => {});
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedUser, selectedDate, page]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingRef.current && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchLogs(nextPage, false, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => { observerRef.current?.disconnect(); };
  }, [hasMore, page, loading, fetchLogs]);

  const formatDate = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch { return dateStr; }
  };

  const formatTime = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }); } catch { return ''; }
  };

  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().split('T')[0];
  });

  const sortedLogs = [...logs].sort((a, b) => {
    const dateA = new Date(a.created_at || 0).getTime();
    const dateB = new Date(b.created_at || 0).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  if (loading) return <Spinner size="large" text={t('followingsLogs.loading')} fullPage />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ fontSize: '14px', color: cellTextColor }}>{error}</p>
        <button style={{ marginTop: '16px', padding: '6px 14px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }} onClick={() => window.location.reload()}>
          {t('followingsLogs.buttons.retry')}
        </button>
      </div>
    );
  }

  if (logs.length === 0 && !limitReached) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ padding: '64px 48px', textAlign: 'center', gap: '12px' }}>
        <div className="flex items-center justify-center" style={{ width: '48px', height: '48px', borderRadius: '12px', background: isDark ? '#1D293D' : '#F1F5F9' }}>
          <Inbox size={24} color={isDark ? '#62748E' : '#90A1B9'} />
        </div>
        <p style={{ fontSize: '14px', fontWeight: 500, color: cellTextColor }}>{t('followingsLogs.emptyState.text', 'No logs available')}</p>
      </div>
    );
  }

  return (
    <>
      {limitReached && (
        <div className="flex items-start" style={{ padding: '16px', gap: '12px', background: infoBg, border: `1px solid ${infoBorder}`, borderRadius: '8px' }}>
          <Info size={20} color={infoText} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: infoText, display: 'block', marginBottom: '4px' }}>{t('followingsLogs.limitWarning.title')}</span>
            <span style={{ fontSize: '14px', color: cellTextColor }}>
              {t('followingsLogs.limitWarning.message')}{' '}
              <button onClick={switchToSettings} style={{ padding: '2px 10px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#FFFFFF', verticalAlign: 'middle' }}>
                {t('followingsLogs.limitWarning.settingsButton')}
              </button>
            </span>
          </div>
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <select value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ width: '100%', height: '32px', padding: '6px 10px', background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: '8px', fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: inputText, appearance: 'none', cursor: 'pointer', outline: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(inputIcon)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', backgroundSize: '20px', paddingRight: '36px' }}>
          <option value="all">{t('followingsLogs.filters.allDates', 'All Dates')}</option>
          {last30Days.map((date) => (<option key={date} value={date}>{formatDate(date)}</option>))}
        </select>
      </div>

      <div style={{ border: `1px solid ${tableBorder}`, borderRadius: '8px', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 12px 6px 6px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <button className="flex items-center" style={{ padding: '6px 10px', gap: '4px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '8px' }} onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('followingsLogs.table.date', 'Date')}</span>
                  <ArrowUpDown size={20} color={titleColor} />
                </button>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('followingsLogs.table.time', 'Time')}</span>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('followingsLogs.table.rule', 'Rule')}</span>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('followingsLogs.table.target', 'Target')}</span>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('followingsLogs.table.action', 'Action')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedLogs.map((log, index) => (
              <tr key={index}>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: cellTextColor }}>{log.created_at && !isNaN(new Date(log.created_at).getTime()) ? formatDate(log.created_at) : '—'}</span>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: cellTextColor }}>{log.created_at && !isNaN(new Date(log.created_at).getTime()) ? formatTime(log.created_at) : '—'}</span>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: cellTextColor }}>{log.rule_info?.rule_name || t('followingsLogs.labels.general', 'Rule Name')}</span>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <a href={`https://www.instagram.com/${log.username || 'unknown'}/`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: linkColor, textDecoration: 'none' }}>
                    @{log.username || t('followingsLogs.labels.unknown')}
                  </a>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: cellTextColor }}>{log.action_description || t('followingsLogs.labels.noDescription', '—')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={loadMoreRef} style={{ padding: '16px', textAlign: 'center', minHeight: '1px' }}>
        {loadingMore && (
          <div className="flex items-center justify-center" style={{ gap: '8px' }}>
            <Loader2 size={18} color={cellTextColor} className="animate-spin" />
            <span style={{ fontSize: '13px', color: cellTextColor }}>{t('followingsLogs.loadingMore', 'Loading more...')}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default FollowingsLogs;
